import { useEffect, useRef, useState } from 'react';

interface TimelineItem {
  id: string;
  age: string;
  title: string;
  content: string[];
  highlight?: string;
  mood: 'dark' | 'struggle' | 'turning' | 'light';
}

const timelineData: TimelineItem[] = [
  {
    id: 'infancia',
    age: '5 anos',
    title: 'A inocência roubada',
    content: [
      'Eu tinha apenas cinco anos. Cinco anos. A idade em que uma criança deveria estar preocupada apenas em brincar, em descobrir o mundo com olhos encantados. Mas o mundo me mostrou sua face mais cruel cedo demais.',
      'Fui vítima de abuso. E aquele momento, que durou segundos na linha do tempo, plantou uma semente de medo que levaria décadas para ser arrancada. Eu não sabia naquele dia, mas aquela experiência iria moldar cada decisão, cada relacionamento, cada noite insone que viria pela frente.',
    ],
    mood: 'dark',
  },
  {
    id: 'panico',
    age: '18 anos',
    title: 'Quando o corpo gritou o que a alma calava',
    content: [
      'Aos 18 anos, o trauma que eu carregava em silêncio encontrou uma forma de se manifestar: a síndrome do pânico. No início, as crises apareciam apenas quando eu viajava de avião. Eu achava que era medo de voar. Não era.',
      'Era o meu corpo gritando o que a minha alma calava há 13 anos. E com o passar do tempo, as crises pioraram. Muito. Já não era só no avião. Era no supermercado. Era no trânsito. Era dentro da minha própria casa.',
    ],
    highlight: 'Era o meu corpo gritando o que a minha alma calava há 13 anos.',
    mood: 'dark',
  },
  {
    id: 'busca',
    age: 'Anos de luta',
    title: 'A busca desesperada por uma saída',
    content: [
      'Fiz de tudo. Remédios convencionais, terapias, psiquiatras. Tomei cada comprimido com a esperança de que aquele seria o último. De que finalmente eu seria livre.',
      'Mas quando parei a medicação, tudo voltou. Como uma onda gigante que você acha que passou, mas estava apenas recuando para voltar com mais força. Eu me sentia presa dentro do meu próprio corpo. Morria de medo de tudo. De sair, de ficar, de dormir, de acordar.',
    ],
    highlight: 'Quando parei a medicação, tudo voltou. Eu me sentia presa dentro do meu próprio corpo.',
    mood: 'struggle',
  },
  {
    id: 'madrugadas',
    age: '30 anos de dor',
    title: 'As madrugadas que ninguém viu',
    content: [
      'Quantas madrugadas eu não fui parar em hospitais achando que estava morrendo — e, ao chegar lá, era constatado: ansiedade?',
      'Quantas vezes me faltava o ar e eu jurava que não sobreviveria àquela noite?',
      'Quantas vezes cheguei a me despedir das minhas filhas por achar que estava morrendo?',
      'Foram 30 anos. Trinta anos de dor, de privações, de uma vida vivida pela metade. Trinta anos em que o medo decidia por mim aonde eu podia ir, o que eu podia fazer, quem eu podia ser.',
    ],
    mood: 'struggle',
  },
  {
    id: 'perda',
    age: 'O dia mais escuro',
    title: 'Quando perdi meu escudo',
    content: [
      'E então, como se a vida quisesse testar meu limite absoluto, meu pai faleceu. Infarto fulminante. Na rua. Sem aviso. Sem despedida.',
      'Aquilo que eu mais temia aconteceu debaixo do meu nariz. O homem que eu mais amei na vida — meu escudo, minha proteção, minha segurança — se foi. Em um instante.',
      'As crises pioraram absurdamente. Além da depressão devastadora pela perda do meu pai, a agonia e a opressão me matavam lentamente por dentro. Cada dia era uma batalha para simplesmente existir.',
    ],
    highlight: 'O homem que eu mais amei na vida — meu escudo, minha proteção, minha segurança — se foi.',
    mood: 'dark',
  },
  {
    id: 'fundo-poco',
    age: 'O fundo do poço',
    title: 'A noite em que desisti',
    content: [
      'Foi então que, após a pior noite de crise da minha vida, eu tomei uma decisão. Eu decidi desistir.',
      'Não tive coragem de tirar minha própria vida — por causa das minhas filhas. Elas eram o único fio que me mantinha aqui. Mas decidi que ficaria deitada. Que esperaria a vida simplesmente passar. Que não lutaria mais.',
      'Eu estava exausta. Exausta de lutar contra um inimigo invisível. Exausta de sorrir quando por dentro eu estava despedaçada. Exausta de fingir que estava tudo bem.',
    ],
    highlight: 'Decidi que ficaria deitada e esperaria a vida simplesmente passar.',
    mood: 'dark',
  },
  {
    id: 'virada',
    age: 'O renascimento',
    title: 'Quando a luz finalmente entrou',
    content: [
      'Mas a vida tinha outros planos para mim. Foi nesse momento de escuridão absoluta que eu descobri um caminho que nunca havia explorado: técnicas de terapias avançadas, programação mental e neurociência aplicada.',
      'Não foi da noite para o dia. Mas pela primeira vez em 30 anos, algo começou a mudar de verdade. Não era um remédio mascarando os sintomas. Era uma transformação real, profunda, que acontecia de dentro para fora.',
      'Eu comecei a entender como meu cérebro funcionava. Comecei a reprogramar padrões que carregava desde os 5 anos. Comecei, finalmente, a me libertar.',
    ],
    mood: 'turning',
  },
  {
    id: 'hoje',
    age: 'Hoje',
    title: 'A mulher que eu me tornei',
    content: [
      'Hoje eu sou livre. Livre do pânico. Livre da ansiedade que me aprisionou por três décadas. Livre do medo que decidia minha vida por mim.',
      'E cada técnica que aprendi, cada ferramenta que me salvou, cada descoberta que mudou minha vida — eu transformei no Carta do Eu Futuro. Porque eu sei o que é viver presa. E sei o que é ser livre.',
    ],
    mood: 'light',
  },
];

const moodStyles = {
  dark: {
    bg: 'bg-luxury-charcoal/40',
    border: 'border-l-red-900/60',
    icon: 'bg-red-900/30 text-red-400/80',
  },
  struggle: {
    bg: 'bg-luxury-charcoal/30',
    border: 'border-l-amber-700/50',
    icon: 'bg-amber-900/30 text-amber-400/70',
  },
  turning: {
    bg: 'bg-gradient-to-br from-luxury-charcoal/30 to-gold-900/10',
    border: 'border-l-gold-500/60',
    icon: 'bg-gold-900/30 text-gold-400',
  },
  light: {
    bg: 'bg-gradient-to-br from-gold-900/15 to-gold-800/5',
    border: 'border-l-gold-500',
    icon: 'bg-gold-500/20 text-gold-400',
  },
};

const TimelineHistoria = () => {
  const [visibleItems, setVisibleItems] = useState<Set<string>>(new Set());
  const [headerVisible, setHeaderVisible] = useState(false);
  const itemRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const headerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const id = entry.target.getAttribute('data-id');
            if (id) {
              setVisibleItems((prev) => new Set(prev).add(id));
            }
            if (entry.target === headerRef.current) {
              setHeaderVisible(true);
            }
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -50px 0px' }
    );

    if (headerRef.current) observer.observe(headerRef.current);
    itemRefs.current.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section className="bg-luxury-black py-24 relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-gold-500/20 to-transparent"></div>
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 left-10 w-72 h-72 bg-gold-500 rounded-full blur-[120px]"></div>
        <div className="absolute bottom-40 right-20 w-96 h-96 bg-gold-600 rounded-full blur-[150px]"></div>
      </div>

      <div className="container mx-auto px-6 lg:px-16 relative z-10">
        {/* DESTAQUE ESPECIAL - CHAMADA DE ATENÇÃO */}
        <div className="max-w-4xl mx-auto mb-16" ref={headerRef}>
          <div
            className="relative bg-gradient-to-br from-gold-500/20 via-gold-600/10 to-transparent border-2 border-gold-500/40 rounded-2xl p-10 lg:p-14 backdrop-blur-md shadow-[0_0_60px_rgba(201,168,76,0.15)] transition-all duration-1000"
            style={{
              opacity: headerVisible ? 1 : 0,
              transform: headerVisible ? 'translateY(0) scale(1)' : 'translateY(30px) scale(0.97)',
            }}
          >
            {/* Ícone decorativo */}
            <div
              className="absolute -top-6 left-1/2 -translate-x-1/2 w-14 h-14 bg-gradient-to-br from-gold-500 to-gold-600 rounded-full flex items-center justify-center shadow-[0_0_30px_rgba(201,168,76,0.4)] transition-all duration-700"
              style={{
                opacity: headerVisible ? 1 : 0,
                transform: headerVisible ? 'translateY(0) scale(1)' : 'translateY(-10px) scale(0.5)',
                transitionDelay: '300ms',
              }}
            >
              <i className="ri-heart-3-fill text-2xl text-luxury-black"></i>
            </div>

            {/* Badge de destaque */}
            <div
              className="flex items-center justify-center gap-3 mb-6 transition-all duration-700"
              style={{
                opacity: headerVisible ? 1 : 0,
                transform: headerVisible ? 'translateY(0)' : 'translateY(15px)',
                transitionDelay: '400ms',
              }}
            >
              <div className="w-12 h-px bg-gradient-to-r from-transparent to-gold-500/60"></div>
              <span className="font-['Montserrat'] text-xs tracking-[0.35em] uppercase text-gold-400 bg-gold-500/10 px-4 py-2 rounded-full border border-gold-500/30">
                Leia com atenção
              </span>
              <div className="w-12 h-px bg-gradient-to-l from-transparent to-gold-500/60"></div>
            </div>

            {/* Título principal */}
            <h2
              className="font-['Playfair_Display'] text-4xl lg:text-5xl text-white font-light text-center leading-tight mb-6 transition-all duration-800"
              style={{
                opacity: headerVisible ? 1 : 0,
                transform: headerVisible ? 'translateY(0)' : 'translateY(20px)',
                filter: headerVisible ? 'blur(0px)' : 'blur(3px)',
                transitionDelay: '550ms',
              }}
            >
              Uma história que <span className="italic text-gold-400">precisava ser contada</span>
            </h2>

            {/* Texto de apoio */}
            <p
              className="font-['Montserrat'] text-base text-white/60 text-center leading-relaxed font-light tracking-wide max-w-2xl mx-auto mb-8 transition-all duration-700"
              style={{
                opacity: headerVisible ? 1 : 0,
                transform: headerVisible ? 'translateY(0)' : 'translateY(15px)',
                transitionDelay: '700ms',
              }}
            >
              Esta não é apenas uma história. É a jornada real de 30 anos de luta, dor e transformação que deu origem ao Carta do Eu Futuro. Cada palavra aqui é verdadeira. Cada momento, vivido.
            </p>

            {/* Indicador de scroll */}
            <div
              className="flex flex-col items-center gap-3 transition-all duration-700"
              style={{
                opacity: headerVisible ? 1 : 0,
                transform: headerVisible ? 'translateY(0)' : 'translateY(10px)',
                transitionDelay: '900ms',
              }}
            >
              <span className="font-['Montserrat'] text-xs text-gold-500/70 uppercase tracking-widest">
                Role para ler
              </span>
              <div className="w-6 h-10 border-2 border-gold-500/40 rounded-full flex items-start justify-center p-2">
                <div className="w-1.5 h-3 bg-gold-500/60 rounded-full animate-bounce"></div>
              </div>
            </div>

            {/* Efeitos decorativos */}
            <div className="absolute top-4 right-4 w-20 h-20 border border-gold-500/20 rounded-full"></div>
            <div className="absolute bottom-4 left-4 w-16 h-16 border border-gold-500/20 rounded-full"></div>
          </div>
        </div>

        {/* Timeline original */}
        <div className="max-w-3xl mx-auto relative">
          {/* Linha vertical da timeline */}
          <div className="absolute left-6 top-0 bottom-0 w-px bg-gradient-to-b from-red-900/40 via-amber-700/30 via-60% to-gold-500/50"></div>

          <div className="space-y-12">
            {timelineData.map((item, index) => {
              const styles = moodStyles[item.mood];
              const isVisible = visibleItems.has(item.id);

              return (
                <div
                  key={item.id}
                  data-id={item.id}
                  ref={(el) => {
                    if (el) itemRefs.current.set(item.id, el);
                  }}
                  className={`relative pl-16 transition-all duration-700 ${
                    isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                  }`}
                  style={{ transitionDelay: `${index * 80}ms` }}
                >
                  {/* Marcador da timeline */}
                  <div
                    className={`absolute left-3 top-6 w-7 h-7 rounded-full flex items-center justify-center ${styles.icon} ring-2 ring-luxury-black`}
                  >
                    <div className="w-2.5 h-2.5 rounded-full bg-current"></div>
                  </div>

                  {/* Card */}
                  <div className={`${styles.bg} border-l-4 ${styles.border} rounded-r-xl p-8 lg:p-10 backdrop-blur-sm`}>
                    <div className="flex items-center gap-3 mb-4">
                      <span className="font-['Montserrat'] text-[10px] uppercase tracking-[0.25em] text-gold-500/70 bg-gold-500/10 px-3 py-1 rounded-full">
                        {item.age}
                      </span>
                    </div>

                    <h3 className="font-['Playfair_Display'] text-2xl lg:text-3xl text-white font-light mb-6">
                      {item.title}
                    </h3>

                    <div className="space-y-4">
                      {item.content.map((paragraph, pIndex) => (
                        <p
                          key={pIndex}
                          className="font-['Montserrat'] text-sm text-white/50 leading-[1.9] font-light tracking-wide"
                        >
                          {paragraph}
                        </p>
                      ))}
                    </div>

                    {item.highlight && (
                      <div className="mt-8 relative pl-6 border-l-2 border-gold-500/30">
                        <p className="font-['Playfair_Display'] text-lg italic text-gold-400/80 leading-relaxed">
                          &ldquo;{item.highlight}&rdquo;
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TimelineHistoria;
